import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdEmotionalWaveGlyphComponent } from './hd-emotional-wave-glyph.component';
import {
  HD_EMOTIONAL_WAVES,
  HD_EMOTIONAL_WAVE_SOURCE_CHANNEL_KEY,
  HdEmotionalWaveKey,
  HdEmotionalWaveRef,
  hdEmotionalWaveColor,
  hdEmotionalWavesInChannels,
} from './hd-emotional-waves.metadata';

interface WaveFieldVm {
  readonly key: string;
  readonly label: string;
  readonly text: string;
}

interface WaveCardVm {
  readonly key: HdEmotionalWaveKey;
  readonly name: string;
  readonly sourceName: string;
  readonly channels: string;
  readonly color: string;
  readonly inChart: boolean;
  readonly fields: readonly WaveFieldVm[];
}

interface SourceLineVm {
  readonly wave: HdEmotionalWaveKey;
  readonly text: string;
}

interface CombinationVm {
  readonly key: string;
  readonly text: string;
  readonly inChart: boolean;
}

interface WavesListVm {
  readonly intro: string;
  readonly inChartLabel: string;
  readonly channelsLabel: string;
  readonly waves: readonly WaveCardVm[];
  readonly source: {
    readonly name: string;
    readonly sourceName: string;
    readonly channels: string;
    readonly color: string;
    readonly description: string;
    readonly inChart: boolean;
    readonly lines: readonly SourceLineVm[];
  };
  readonly combinationsTitle: string;
  readonly combinationsIntro: string;
  readonly combinations: readonly CombinationVm[];
  readonly openTitle: string;
  readonly open: string;
}

const WAVE_FIELDS = ['shape', 'theme', 'expression', 'pitfall', 'clarity'] as const;

/** Paren van golven met een eigen combinatietekst, in de volgorde van de bron. */
const WAVE_COMBINATIONS: readonly (readonly [HdEmotionalWaveKey, HdEmotionalWaveKey])[] = [
  ['tribal', 'individual'],
  ['tribal', 'collective'],
  ['individual', 'collective'],
];

/**
 * Overzicht van de drie emotionele golven (tribaal, individueel, collectief),
 * poort 6 als bron, de combinaties en de open Solar Plexus. Gedeeld door de
 * Solar Plexus-sectie van het centrum-infopaneel en het golvenpaneel.
 *
 * Met {@link definedChannelKeys} krijgen de golven van de chart een badge "in
 * jouw chart" en worden de passende combinaties uitgelicht; zonder (onbekende
 * chart-context) blijft de lijst neutraal. Elke golfsectie draagt
 * `data-emotional-wave` als scroll-anker voor het paneel.
 */
@Component({
  selector: 'hd-emotional-waves-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdEmotionalWaveGlyphComponent],
  templateUrl: './hd-emotional-waves-list.component.html',
  styleUrl: './hd-emotional-waves-list.component.scss',
})
export class HdEmotionalWavesListComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Gedefinieerde kanalen van de chart; `undefined` = onbekend (geen badges). */
  readonly definedChannelKeys = input<readonly string[] | undefined>(undefined);

  private readonly wavesInChart = computed<ReadonlySet<HdEmotionalWaveRef>>(() => {
    const channelKeys = this.definedChannelKeys();
    return new Set(channelKeys ? hdEmotionalWavesInChannels(channelKeys) : []);
  });

  protected readonly vm = computed<WavesListVm>(() => {
    const inChart = this.wavesInChart();
    return {
      intro: this.text('panel.intro'),
      inChartLabel: this.text('panel.inChart'),
      channelsLabel: this.text('panel.channelsLabel'),
      waves: HD_EMOTIONAL_WAVES.map(wave => ({
        key: wave.key,
        name: this.text(`waves.${wave.key}.name`),
        sourceName: this.text(`waves.${wave.key}.sourceName`),
        channels: wave.channelKeys.join(', '),
        color: hdEmotionalWaveColor(wave.key),
        inChart: inChart.has(wave.key),
        fields: WAVE_FIELDS.map(field => ({
          key: field,
          label: this.text(`panel.${field}Label`),
          text: this.text(`waves.${wave.key}.${field}`),
        })),
      })),
      source: {
        name: this.text('source.name'),
        sourceName: this.text('source.sourceName'),
        channels: HD_EMOTIONAL_WAVE_SOURCE_CHANNEL_KEY,
        color: hdEmotionalWaveColor('source'),
        description: this.text('source.description'),
        inChart: inChart.has('source'),
        lines: HD_EMOTIONAL_WAVES.map(wave => ({
          wave: wave.key,
          text: this.text(`source.lines.${wave.key}`),
        })),
      },
      combinationsTitle: this.text('panel.combinationsTitle'),
      combinationsIntro: this.text('panel.combinationsIntro'),
      combinations: [
        ...WAVE_COMBINATIONS.map(([a, b]) => ({
          key: `${a}-${b}`,
          text: this.text(`combinations.${a}-${b}`),
          inChart: inChart.has(a) && inChart.has(b),
        })),
        { key: 'source', text: this.text('combinations.source'), inChart: inChart.has('source') },
      ],
      openTitle: this.text('panel.openTitle'),
      open: this.text('panel.open'),
    };
  });

  constructor() {
    // Beide content-talen eager laden zodat taalwissel én NL-fallback meteen
    // correcte tekst opleveren (idempotent, zie loadScopesForLang).
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('emotionalWaves', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('emotionalWaves', key, 'nl');
  }
}
